'use client';

import { useState } from 'react';
import { RiErrorWarningFill } from '@remixicon/react';
import { QueryCache, QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { toast } from 'sonner';
import { Alert, AlertIcon, AlertTitle } from '@/components/ui/alert';
import {
  createIDBPersister,
  shouldPersistQuery,
  QUERY_PERSIST_MAX_AGE,
  QUERY_PERSIST_BUSTER,
} from '@/lib/query-persist';

const QueryProvider = ({ children }) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            const message =
              error.message || 'Something went wrong. Please try again.';

            toast.custom(
              () => (
                <Alert variant="mono" icon="destructive" close={false}>
                  <AlertIcon>
                    <RiErrorWarningFill />
                  </AlertIcon>
                  <AlertTitle>{message}</AlertTitle>
                </Alert>
              ),

              {
                position: 'top-center',
              },
            );
          },
        }),
      }),
  );

  // Level-1 offline: persist opt-in (meta.persist) queries to IndexedDB so already
  // loaded views render from cache while offline. Wiped on logout (see
  // lib/query-persist + BackendAuth.logout). buster = build hash → deploy busts it.
  const [persistOptions] = useState(() => ({
    persister: createIDBPersister(),
    maxAge: QUERY_PERSIST_MAX_AGE,
    buster: QUERY_PERSIST_BUSTER,
    dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
  }));

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={persistOptions}
    >
      {children}
    </PersistQueryClientProvider>
  );
};

export { QueryProvider };
